'use client';
import React, { useState, useEffect } from 'react';

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/tarieven', label: 'Tarieven' },
    { href: '/contact', label: 'Contact' },
    { href: '/interactief', label: '🎮 Interactief' },
  ];

  return (
    <>
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-gray-950/95 backdrop-blur-md shadow-lg shadow-black/20'
            : 'bg-gray-950'
        }`}
      >
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 flex items-center h-16 md:h-18 gap-4">
          {/* Logo */}
          <a
            href="/"
            aria-label="Rijschool Simson"
            className="flex-shrink-0 flex items-center"
          >
            <div
              className="w-28 md:w-36 h-10 bg-contain bg-no-repeat bg-left"
              style={{
                backgroundImage: `url('https://d1mb7prhlgzbgn.cloudfront.net/rails/active_storage/representations/proxy/eyJfcmFpbHMiOnsiZGF0YSI6MTI3NDksInB1ciI6ImJsb2JfaWQifX0=--0fddda993116fa4b7d266ba97afaf8fa7b813d8d/eyJfcmFpbHMiOnsiZGF0YSI6eyJjb252ZXJ0Ijoid2VicCIsInJlc2l6ZV90b19maXQiOls0MDAsNDAwXX0sInB1ciI6InZhcmlhdGlvbiJ9fQ==--6dfce9f95e70be9e5a1f89e9d6694743ae05467d/logo.png')`,
                filter: 'brightness(0) invert(1)',
              }}
            />
          </a>

          {/* Desktop nav links — center */}
          <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
            {navLinks?.map((link) => (
              <a
                key={link?.href}
                href={link?.href}
                className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-150"
              >
                {link?.label}
              </a>
            ))}
          </nav>

          {/* Right side: phone + CTA */}
          <div className="hidden md:flex items-center gap-3 flex-shrink-0">
            <a
              href="tel:0620564200"
              className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-brand">
                <path fillRule="evenodd" d="M2 3.5A1.5 1.5 0 013.5 2h1.148a1.5 1.5 0 011.465 1.175l.716 3.223a1.5 1.5 0 01-1.052 1.767l-.933.267c-.41.117-.643.555-.48.95a11.542 11.542 0 006.254 6.254c.395.163.833-.07.95-.48l.267-.933a1.5 1.5 0 011.767-1.052l3.223.716A1.5 1.5 0 0118 15.352V16.5a1.5 1.5 0 01-1.5 1.5H15c-1.149 0-2.263-.15-3.326-.43A13.022 13.022 0 012.43 8.326 13.019 13.019 0 012 5V3.5z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">06 20564200</span>
            </a>
          </div>

          {/* Mobile: hamburger */}
          <div className="flex items-center gap-2 ml-auto md:hidden">
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menu"
              className="w-10 h-10 flex items-center justify-center text-gray-300 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-150"
            >
              {menuOpen ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                  <path fillRule="evenodd" d="M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10zm0 5.25a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75a.75.75 0 01-.75-.75z" clipRule="evenodd" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {menuOpen && (
          <div className="md:hidden bg-gray-900 border-t border-white/10 px-4 py-3">
            <nav className="flex flex-col gap-1">
              {navLinks?.map((link) => (
                <a
                  key={link?.href}
                  href={link?.href}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center h-11 px-4 text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-150 active:scale-95 active:bg-brand/20 active:text-brand select-none"
                >
                  {link?.label}
                </a>
              ))}
              <div className="border-t border-white/10 mt-2 pt-3 pb-1">
                <a
                  href="tel:0620564200"
                  className="flex items-center gap-2 h-11 px-4 text-sm text-gray-400 hover:text-white rounded-xl transition-all duration-150"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-brand">
                    <path fillRule="evenodd" d="M2 3.5A1.5 1.5 0 013.5 2h1.148a1.5 1.5 0 011.465 1.175l.716 3.223a1.5 1.5 0 01-1.052 1.767l-.933.267c-.41.117-.643.555-.48.95a11.542 11.542 0 006.254 6.254c.395.163.833-.07.95-.48l.267-.933a1.5 1.5 0 011.767-1.052l3.223.716A1.5 1.5 0 0118 15.352V16.5a1.5 1.5 0 01-1.5 1.5H15c-1.149 0-2.263-.15-3.326-.43A13.022 13.022 0 012.43 8.326 13.019 13.019 0 012 5V3.5z" clipRule="evenodd" />
                  </svg>
                  06 20564200
                </a>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
